import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { SEO } from "../components/SEO";
import { PageHeader } from "../components/PageHeader";
import { CTA } from "../components/sections/CTA";
import { Reveal } from "../components/motion/Reveal";
import { Stagger, StaggerItem } from "../components/motion/Stagger";
import { Counter } from "../components/motion/Counter";
import { Magnetic } from "../components/motion/Magnetic";
import {
  IconArrow,
  IconBed,
  IconCamera,
  IconCheck,
  IconClock,
  IconEuro,
  IconKey,
  IconLeaf,
  IconPhone,
  IconShield,
  IconSparkle,
} from "../components/Icon";
import { site } from "../lib/site";
import { breadcrumbJsonLd, faqJsonLd, localBusinessJsonLd, serviceJsonLd } from "../lib/structured-data";

const included = [
  {
    icon: IconSparkle,
    title: "Ménage complet du logement",
    text: "Sols, sanitaires, cuisine, surfaces, vitrages intérieurs, poussières et sortie des poubelles.",
  },
  {
    icon: IconBed,
    title: "Changement des draps & linge",
    text: "Literie faite au carré, serviettes & tapis de bain renouvelés. Linge fourni ou loué selon votre préférence.",
  },
  {
    icon: IconKey,
    title: "Réassort des consommables",
    text: "Café, capsules, papier toilette, essuie-tout, savon, gel douche, produits ménagers de secours.",
  },
  {
    icon: IconCamera,
    title: "Check photos & rapport",
    text: "Envoi d'un rapport photo à chaque rotation : preuve de propreté, signalement des oublis ou dégradations.",
  },
  {
    icon: IconClock,
    title: "Respect du créneau 11h – 15h",
    text: "Nous garantissons la fenêtre de turnover Airbnb pour que votre logement soit prêt à l'heure du check-in.",
  },
  {
    icon: IconLeaf,
    title: "Produits éco-labellisés",
    text: "Dosage maîtrisé, microfibres, produits respectueux des surfaces et des voyageurs sensibles.",
  },
];

const process = [
  {
    n: "01",
    title: "Check-out voyageur",
    text: "Vous ou la plateforme nous confirmez l'heure de départ. Nous prévoyons notre passage pour être sur place dans l'heure qui suit.",
  },
  {
    n: "02",
    title: "Rotation complète",
    text: "Ménage, literie, consommables, état des lieux visuel. Un binôme intervient sur les logements T3 et plus pour tenir les délais.",
  },
  {
    n: "03",
    title: "Rapport photo",
    text: "Envoi d'un album (pièces + plans rapprochés) sur WhatsApp, email ou Slack avant 15 h. Signalement de toute dégradation.",
  },
  {
    n: "04",
    title: "Check-in voyageur",
    text: "Le logement est prêt. Vous n'avez qu'à donner le code d'accès - nous restons joignables en cas d'imprévu voyageur.",
  },
];

const pains = [
  {
    problem: "Ménage bâclé entre deux voyageurs",
    solution: "Check-list détaillée + rapport photo à chaque rotation, reprise sous 24 h si non conforme.",
  },
  {
    problem: "Créneau de turnover trop court",
    solution: "Planning horaire optimisé, binôme disponible pour les grands logements, arrivée ≤ 1 h après check-out.",
  },
  {
    problem: "Linge sale ou oublié",
    solution: "Stock de linge hôtelier propre, option location complète, plis au carré façon hôtel.",
  },
  {
    problem: "Dégradations non signalées",
    solution: "Rapport photo daté qui sert de preuve pour vos demandes AirCover / caution.",
  },
  {
    problem: "Consommables en rupture",
    solution: "Réassort automatique inclus (café, papier, savon). Listing mensuel des coûts refacturés à prix coûtant.",
  },
  {
    problem: "Voyageur mécontent à minuit",
    solution: "Ligne directe 7j/7, intervention d'urgence possible selon disponibilité et distance.",
  },
];

const airbnbFaq = [
  {
    q: "Sur quelles plateformes intervenez-vous ?",
    a: "Airbnb, Booking, Abritel, Gîtes de France, Homelidays, ou location directe - nous nous adaptons à votre canal principal. Un accès calendrier partagé (iCal / Smoobu / Lodgify) nous permet de synchroniser automatiquement nos passages.",
  },
  {
    q: "Quel est votre tarif pour un ménage Airbnb ?",
    a: "Chaque rotation est chiffrée sur mesure : il n'y a pas de barème fixe car le tarif dépend de la surface, du nombre de chambres, des options (linge fourni/loué, consommables) et de la fréquence moyenne de rotation. Devis ferme sous 48 h après une visite ou un échange.",
  },
  {
    q: "Fournissez-vous le linge ?",
    a: "Oui. Trois options : (1) vous nous remettez votre linge propre, (2) nous louons le linge hôtelier à votre nom via notre partenaire blanchisserie, (3) formule mixte. Le linge loué est changé à chaque rotation et facturé au volume.",
  },
  {
    q: "Combien de temps dure un ménage type ?",
    a: "Un studio : 45 min. Un T2 : 1 h à 1 h 15. Un T3 : 1 h 30 à 2 h. Un binôme peut réduire ce temps de moitié pour tenir un créneau serré.",
  },
  {
    q: "Que se passe-t-il si un voyageur a fait des dégâts ?",
    a: "Nous documentons photographiquement toute dégradation (mobilier cassé, taches importantes, objets manquants) et vous envoyons le rapport immédiatement. Ces preuves sont utilisables pour ouvrir une réclamation Airbnb AirCover ou activer la caution.",
  },
  {
    q: "Gérez-vous les urgences en dehors des horaires de bureau ?",
    a: "Oui, 7j/7 en haute saison. Une ligne WhatsApp vous connecte directement à votre interlocuteur jusqu'à 22 h. En cas d'urgence type fuite ou serrure cassée, nous orientons vers nos artisans partenaires.",
  },
  {
    q: "Êtes-vous assurés pour ce type de prestation ?",
    a: "Oui, responsabilité civile professionnelle couvrant les biens confiés, les tiers et les clés. Le protocole de turnover courte durée est rigoureusement respecté à chaque rotation.",
  },
];

const ease = [0.22, 1, 0.36, 1] as const;

export function Airbnb() {
  return (
    <>
      <SEO
        title="Nettoyage Airbnb à Vichy - Conciergerie ménage 7j/7 (linge, check photos)"
        description="Ménage entre deux locations courtes durées à Vichy : turnover Airbnb / Booking, linge hôtelier, réassort consommables, rapport photo. Disponible 7j/7, créneau 11h-15h respecté."
        path="/nettoyage-airbnb"
        jsonLd={[
          localBusinessJsonLd(),
          serviceJsonLd(
            "Nettoyage Airbnb & locations courtes durées",
            "Rotation de ménage entre deux voyageurs à Vichy : ménage complet, linge, réassort, rapport photo. 7j/7, créneau 11h-15h.",
            "nettoyage-airbnb-location-courte-duree",
          ),
          faqJsonLd(airbnbFaq),
          breadcrumbJsonLd([
            { name: "Accueil", url: `${site.baseUrl}/` },
            { name: "Services", url: `${site.baseUrl}/services` },
            { name: "Nettoyage Airbnb", url: `${site.baseUrl}/nettoyage-airbnb` },
          ]),
        ]}
      />

      <PageHeader
        eyebrow="Conciergerie ménage · Airbnb, Booking, Abritel"
        title="Nettoyage Airbnb à Vichy, sans prise de tête"
        subtitle="Vichy attire les curistes et les week-ends. Chaque rotation doit être parfaite, tenue dans la fenêtre check-out / check-in et documentée. Nous nous en occupons - vous gérez votre planning."
        breadcrumbs={[
          { label: "Accueil", to: "/" },
          { label: "Services", to: "/services" },
          { label: "Nettoyage Airbnb" },
        ]}
      />

      {/* Quick stats + CTAs */}
      <section className="section bg-white">
        <div className="container-page grid lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-7">
            <h2 className="text-3xl">Une conciergerie ménage pensée pour les hôtes locaux</h2>
            <p className="mt-5 text-ink-muted text-lg leading-relaxed">
              Entre un curiste qui part à 11 h et un couple qui arrive à 15 h, vous avez quatre
              heures pour que tout soit impeccable. Anexya s'occupe du ménage complet, du linge,
              des consommables et du rapport photo - pour que votre note reste au sommet, même
              en haute saison.
            </p>

            <ul className="mt-7 grid sm:grid-cols-2 gap-3">
              {[
                "Rotation complète en moins d'une heure (studio)",
                "Linge hôtelier fourni ou loué",
                "Rapport photo à chaque passage",
                "Synchro calendrier iCal / Smoobu",
                "Disponible 7j/7 en saison",
                "Devis gratuit sous 48 h",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-ink">
                  <span className="mt-0.5 grid h-6 w-6 place-items-center rounded-full bg-accent-500/15 text-accent-700 ring-1 ring-accent-500/30 flex-none">
                    <IconCheck className="h-3.5 w-3.5" strokeWidth={2.5} />
                  </span>
                  <span className="text-sm font-medium">{t}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap gap-3">
              <Magnetic>
                <Link to="/contact" className="btn-primary group">
                  Demander un devis conciergerie
                  <IconArrow className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </Magnetic>
              <a href={site.phoneHref} className="btn-ghost">
                <IconPhone className="h-4 w-4" />
                {site.phoneDisplay}
              </a>
            </div>
          </Reveal>

          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease }}
          >
            <div className="rounded-3xl bg-gradient-to-br from-brand-700 via-brand-600 to-brand-800 text-white p-6 sm:p-8 shadow-soft-lg overflow-hidden relative">
              <div
                className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-accent-500/30 blur-3xl"
                aria-hidden="true"
              />
              <div className="relative">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-xs font-semibold uppercase tracking-wider border border-white/20">
                  Saison thermale Vichy
                </span>
                <div className="mt-5 grid grid-cols-2 gap-4">
                  <StatCard value={<><Counter to={45} /> min</>} label="Ménage studio moyen" />
                  <StatCard value={<><Counter to={4} /> h</>} label="Fenêtre check-out / check-in" />
                  <StatCard value="7j/7" label="Haute saison mai – oct." />
                  <StatCard value={<><Counter to={100} />%</>} label="Rotations photographiées" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Ce qui est inclus */}
      <section className="section bg-surface-soft relative overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-24 left-1/3 h-80 w-80 rounded-full bg-brand-300/20 blur-3xl"
        />
        <div className="container-page relative">
          <Reveal as="header" className="max-w-2xl">
            <span className="eyebrow">Ce qui est inclus</span>
            <h2 className="mt-4">Une rotation complète, rien à gérer de votre côté</h2>
            <p className="mt-4 text-ink-muted text-lg">
              Chaque prestation suit la même check-list validée avec vous au démarrage. Les
              options (linge, consommables, fréquence) sont ajustables mois par mois.
            </p>
          </Reveal>

          <Stagger className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {included.map((it) => (
              <StaggerItem key={it.title}>
                <motion.div
                  whileHover={{ y: -4 }}
                  transition={{ type: "spring", stiffness: 260, damping: 22 }}
                  className="card card-hover h-full"
                >
                  <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-brand-100">
                    <it.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 text-lg">{it.title}</h3>
                  <p className="mt-2 text-sm text-ink-muted leading-relaxed">{it.text}</p>
                </motion.div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Processus */}
      <section className="section bg-white">
        <div className="container-page">
          <Reveal as="header" className="max-w-2xl">
            <span className="eyebrow">Notre processus</span>
            <h2 className="mt-4">De 11h à 15h : que se passe-t-il exactement ?</h2>
          </Reveal>

          <Stagger as="ol" className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {process.map((s) => (
              <StaggerItem key={s.n} as="li">
                <motion.div
                  whileHover={{ y: -4 }}
                  transition={{ type: "spring", stiffness: 260, damping: 22 }}
                  className="relative h-full rounded-2xl bg-gradient-to-br from-brand-50 via-white to-white border border-brand-100 p-6 overflow-hidden"
                >
                  <div
                    aria-hidden="true"
                    className="absolute -top-6 -right-6 h-24 w-24 rounded-full bg-brand-200/40 blur-2xl"
                  />
                  <div className="relative font-display font-bold text-4xl bg-gradient-to-br from-brand-400 to-brand-700 bg-clip-text text-transparent">
                    {s.n}
                  </div>
                  <h3 className="relative mt-2 text-lg">{s.title}</h3>
                  <p className="relative mt-2 text-sm text-ink-muted leading-relaxed">{s.text}</p>
                </motion.div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Pain points / Solutions */}
      <section className="section bg-surface-soft">
        <div className="container-page">
          <Reveal as="header" className="max-w-2xl">
            <span className="eyebrow">Problème / Solution</span>
            <h2 className="mt-4">Les galères classiques d'un hôte, réglées</h2>
          </Reveal>

          <Stagger className="mt-10 grid gap-4 md:grid-cols-2">
            {pains.map((p) => (
              <StaggerItem key={p.problem}>
                <motion.div
                  whileHover={{ x: 3 }}
                  transition={{ type: "spring", stiffness: 260, damping: 22 }}
                  className="rounded-2xl bg-white border border-slate-200 p-5 flex gap-4 h-full"
                >
                  <div className="grid h-10 w-10 place-items-center rounded-lg bg-accent-500/15 text-accent-700 flex-none ring-1 ring-accent-500/30">
                    <IconShield className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-brand-900 text-sm">{p.problem}</div>
                    <div className="mt-1 text-sm text-ink-muted leading-relaxed">→ {p.solution}</div>
                  </div>
                </motion.div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Formules */}
      <section className="section bg-white">
        <div className="container-page">
          <Reveal as="header" className="max-w-2xl">
            <span className="eyebrow">Formules</span>
            <h2 className="mt-4">Trois façons de travailler ensemble</h2>
            <p className="mt-4 text-ink-muted text-lg">
              Pas de barème fixe : chaque logement est chiffré sur mesure. Nous établissons un
              devis ferme sous 48 h après une visite ou l'envoi des plans.
            </p>
          </Reveal>

          <div className="mt-10 grid gap-5 md:grid-cols-3">
            <PriceCard
              tag="Rotation simple"
              price="Sur devis"
              bullets={[
                "Ménage complet du logement",
                "Literie faite (linge fourni par vous)",
                "Rapport photo envoyé à l'hôte",
                "Passage à la demande",
              ]}
            />
            <PriceCard
              tag="Rotation + linge"
              price="Sur devis"
              featured
              bullets={[
                "Tout de la formule simple",
                "Location du linge hôtelier",
                "Consommables réassortis à prix coûtant",
                "Synchro calendrier iCal",
                "Intervention 7j/7",
              ]}
            />
            <PriceCard
              tag="Conciergerie"
              price="Sur devis"
              bullets={[
                "Tout de la formule précédente",
                "Gestion des clés / box",
                "Mise en place entre hôtes",
                "Astreinte WhatsApp voyageur",
                "Refacturation des consommables",
              ]}
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section bg-surface-soft">
        <div className="container-page max-w-4xl">
          <Reveal as="header" className="max-w-2xl">
            <span className="eyebrow">Questions fréquentes - hôtes</span>
            <h2 className="mt-4">Ce que les hôtes Airbnb nous demandent le plus</h2>
          </Reveal>

          <div className="mt-10 grid gap-3">
            {airbnbFaq.map((item) => (
              <details
                key={item.q}
                className="group rounded-2xl bg-white border border-slate-200 open:shadow-soft transition-shadow"
              >
                <summary className="flex items-center gap-4 px-5 sm:px-6 py-5 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                  <span className="flex-1 font-display font-semibold text-brand-900 text-base sm:text-lg">
                    {item.q}
                  </span>
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-brand-50 text-brand-700 flex-none group-hover:bg-brand-100 transition-colors group-open:rotate-45 duration-200">
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </span>
                </summary>
                <div className="px-5 sm:px-6 pb-5 text-ink-muted leading-relaxed text-sm">
                  {item.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CTA />
    </>
  );
}

function StatCard({ value, label }: { value: React.ReactNode; label: string }) {
  return (
    <div className="rounded-2xl bg-white/10 backdrop-blur border border-white/20 p-4">
      <div className="font-display font-bold text-2xl text-white">{value}</div>
      <div className="text-xs text-brand-100 mt-1">{label}</div>
    </div>
  );
}

function PriceCard({
  tag,
  price,
  unit,
  bullets,
  featured = false,
}: {
  tag: string;
  price: string;
  unit?: string;
  bullets: string[];
  featured?: boolean;
}) {
  return (
    <motion.div
      whileHover={{ y: -6 }}
      transition={{ type: "spring", stiffness: 260, damping: 22 }}
      className={`relative rounded-2xl p-6 sm:p-7 h-full border ${
        featured
          ? "bg-gradient-to-br from-brand-700 to-brand-800 text-white border-brand-600 shadow-soft-lg"
          : "bg-white border-slate-200 shadow-soft"
      }`}
    >
      {featured && (
        <span className="absolute -top-3 right-5 inline-flex items-center gap-1.5 rounded-full bg-accent-500 text-white px-3 py-1 text-xs font-semibold uppercase tracking-wider shadow-soft">
          Plus demandée
        </span>
      )}
      <div
        className={`text-xs uppercase tracking-wider font-semibold ${
          featured ? "text-brand-100" : "text-brand-700"
        }`}
      >
        {tag}
      </div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span
          className={`font-display font-bold text-3xl sm:text-4xl ${
            featured ? "text-white" : "text-brand-900"
          }`}
        >
          {price}
        </span>
        {unit && (
          <span
            className={`text-sm ${featured ? "text-brand-100" : "text-ink-muted"}`}
          >
            {unit}
          </span>
        )}
      </div>
      <ul className="mt-5 space-y-2.5">
        {bullets.map((b) => (
          <li
            key={b}
            className={`flex items-start gap-2.5 text-sm ${
              featured ? "text-brand-50" : "text-ink"
            }`}
          >
            <span
              className={`mt-0.5 grid h-5 w-5 place-items-center rounded-full flex-none ${
                featured
                  ? "bg-white/20 text-white"
                  : "bg-accent-500/15 text-accent-700 ring-1 ring-accent-500/30"
              }`}
            >
              <IconCheck className="h-3 w-3" strokeWidth={3} />
            </span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
      {!featured && (
        <div className="mt-6 flex items-center gap-2 text-xs text-ink-soft">
          <IconEuro className="h-4 w-4" />
          <span>Paiement mensuel sur facture</span>
        </div>
      )}
    </motion.div>
  );
}
